import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import { AddressAutocomplete } from "../../../components/dashboard/AddressAutocomplete";
import { MediaPreview } from "../../../components/dashboard/MediaPreview";
import { Modal } from "../../../components/dashboard/Modal";
import { PlaylistPreview } from "../../../components/dashboard/PlaylistPreview";
import { useConfirm } from "../../../contexts/ConfirmContext";
import { useToast } from "../../../contexts/ToastContext";
import { extractErrorMessage } from "../../../lib/api";
import {
  attachMediaToAdvertiser,
  attachPlaylistsToAdvertiser,
  deleteAdvertiser,
  detachMediaFromAdvertiser,
  detachPlaylistFromAdvertiser,
  getAdvertiserMedia,
  getAdvertiserPlaylists,
  listAdvertisers,
  listMedia,
  listPlaylists,
  saveAdvertiser,
  uploadMedia,
} from "../../../lib/dashboardApi";
import { avatarColor, initials } from "../../../lib/format";
import { hasMinRole } from "../../../lib/hooks/useAuthMe";
import type { Advertiser, MediaItem, Playlist, User } from "../../../types/dashboard";

type Ctx = { user?: User };

export function Advertisers() {
  const { user } = useOutletContext<Ctx>();
  const canEdit = hasMinRole(user, "editor");
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Advertiser> | null>(null);
  const [nameError, setNameError] = useState("");
  const [saving, setSaving] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: list = [], isLoading } = useQuery({
    queryKey: ["advertisers"],
    queryFn: listAdvertisers,
  });

  // Client-side filter — the list endpoint returns every advertiser, so search
  // matches across the fields shown on the card (name, email, phone).
  const query = search.trim().toLowerCase();
  const filtered = query
    ? list.filter((a) =>
        [a.name, a.email, a.phone].some((f) => f?.toLowerCase().includes(query)),
      )
    : list;

  function openCreate() {
    setEditing({});
    setNameError("");
    setModalOpen(true);
  }
  function openEdit(a: Advertiser) {
    setEditing({ ...a });
    setNameError("");
    setModalOpen(true);
  }

  async function save() {
    if (!editing?.name?.trim()) {
      setNameError("Please enter a business name.");
      return;
    }
    setSaving(true);
    try {
      await saveAdvertiser(editing);
      qc.invalidateQueries({ queryKey: ["advertisers"] });
      toast(editing.id ? "Advertiser updated." : "Advertiser created.");
      setModalOpen(false);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not save."), "error");
    } finally {
      setSaving(false);
    }
  }

  async function remove(a: Advertiser) {
    if (
      !(await confirm({
        title: "Delete advertiser?",
        message: `Delete ${a.name}? Their media will be unassigned, not deleted.`,
        danger: true,
      }))
    )
      return;
    try {
      await deleteAdvertiser(a.id);
      qc.invalidateQueries({ queryKey: ["advertisers"] });
      toast("Deleted.");
      if (selectedId === a.id) setSelectedId(null);
    } catch (e) {
      toast(extractErrorMessage(e, "Could not delete."), "error");
    }
  }

  const selected = selectedId ? list.find((a) => a.id === selectedId) : null;

  if (selected) {
    return (
      <AdvertiserDetail
        advertiser={selected}
        canEdit={canEdit}
        onBack={() => setSelectedId(null)}
        onEdit={() => openEdit(selected)}
        onDelete={() => remove(selected)}
        onOpenAttach={() => setAttachOpen(true)}
        attachOpen={attachOpen}
        closeAttach={() => setAttachOpen(false)}
      >
        <AdvertiserModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          editing={editing}
          setEditing={setEditing}
          nameError={nameError}
          setNameError={setNameError}
          onSave={save}
          saving={saving}
        />
      </AdvertiserDetail>
    );
  }

  return (
    <>
      <h2 className="page-title">Advertisers</h2>
      <div className="card">
        <div className="card-header">
          <h3>
            All Advertisers{" "}
            <span style={{ fontWeight: 400, color: "var(--text2)", fontSize: 13 }}>
              {filtered.length}
            </span>
          </h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {list.length > 0 && (
              <input
                className="form-input list-search"
                type="search"
                placeholder="Search advertisers…"
                aria-label="Search advertisers"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            )}
            {canEdit && (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                + New Advertiser
              </button>
            )}
          </div>
        </div>
        {isLoading ? (
          <div className="empty">Loading…</div>
        ) : list.length === 0 ? (
          <div className="adv-empty">
            <div className="adv-empty-title">No advertisers yet</div>
            <div className="adv-empty-desc">
              Add advertisers to organize your media library by business.
            </div>
            {canEdit && (
              <button type="button" className="btn btn-primary" onClick={openCreate}>
                Add Your First Advertiser
              </button>
            )}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty">No advertisers match “{search.trim()}”.</div>
        ) : (
          <div className="adv-grid">
            {filtered.map((a) => (
              <button
                key={a.id}
                type="button"
                className="adv-card"
                onClick={() => setSelectedId(a.id)}
                style={{
                  textAlign: "left",
                  fontFamily: "inherit",
                  border: "1px solid var(--border)",
                }}
              >
                <div className="adv-avatar" style={{ background: avatarColor(a.name) }}>
                  {initials(a.name)}
                </div>
                <div className="adv-card-body">
                  <div className="adv-card-name">{a.name}</div>
                  <div className="adv-card-contact">
                    {a.email ?? a.phone ?? "No contact info"}
                  </div>
                  <div className="adv-card-stats">
                    <span>{a.media_count ?? 0} media</span>
                    <span>{a.playlist_count ?? 0} playlists</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      <AdvertiserModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editing={editing}
        setEditing={setEditing}
        nameError={nameError}
        setNameError={setNameError}
        onSave={save}
        saving={saving}
      />
    </>
  );
}

function AdvertiserModal({
  open,
  onClose,
  editing,
  setEditing,
  nameError,
  setNameError,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  editing: Partial<Advertiser> | null;
  setEditing: (a: Partial<Advertiser> | null) => void;
  nameError: string;
  setNameError: (s: string) => void;
  onSave: () => void;
  saving: boolean;
}) {
  if (!editing) return null;
  return (
    <Modal open={open} onClose={onClose} width={520} ariaLabel="Advertiser form">
      <h3>{editing.id ? "Edit Advertiser" : "New Advertiser"}</h3>
      <div className="form-group">
        <label>
          Business Name <span style={{ color: "var(--red)" }}>*</span>
        </label>
        <input
          className={nameError ? "form-input error" : "form-input"}
          value={editing.name ?? ""}
          onChange={(e) => {
            setEditing({ ...editing, name: e.target.value });
            if (nameError) setNameError("");
          }}
          placeholder="e.g. Joe's Pizza"
          autoFocus
        />
        {nameError && <div className="form-error" style={{ display: "block" }}>{nameError}</div>}
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Phone</label>
          <input
            className="form-input"
            value={editing.phone ?? ""}
            onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
            type="tel"
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            className="form-input"
            value={editing.email ?? ""}
            onChange={(e) => setEditing({ ...editing, email: e.target.value })}
            type="email"
          />
        </div>
      </div>
      <div className="form-group">
        <label>Address</label>
        <AddressAutocomplete
          value={editing.address ?? ""}
          onChange={(v) => setEditing({ ...editing, address: v })}
        />
      </div>
      <div className="form-group">
        <label>Notes</label>
        <textarea
          className="form-input"
          rows={2}
          style={{ resize: "vertical" }}
          value={editing.notes ?? ""}
          onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
        />
      </div>
      <div className="modal-actions">
        <button type="button" className="btn btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : editing.id ? "Update" : "Add Advertiser"}
        </button>
      </div>
    </Modal>
  );
}

function AdvertiserDetail({
  advertiser,
  canEdit,
  onBack,
  onEdit,
  onDelete,
  onOpenAttach,
  attachOpen,
  closeAttach,
  children,
}: {
  advertiser: Advertiser;
  canEdit: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onOpenAttach: () => void;
  attachOpen: boolean;
  closeAttach: () => void;
  children?: React.ReactNode;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [playlistAttachOpen, setPlaylistAttachOpen] = useState(false);
  const [previewPlaylist, setPreviewPlaylist] = useState<Playlist | null>(null);
  const { data: items = [], refetch } = useQuery({
    queryKey: ["advertiser-media", advertiser.id],
    queryFn: () => getAdvertiserMedia(advertiser.id),
  });
  const { data: playlists = [], refetch: refetchPlaylists } = useQuery({
    queryKey: ["advertiser-playlists", advertiser.id],
    queryFn: () => getAdvertiserPlaylists(advertiser.id),
  });

  async function detachPlaylist(pl: Playlist) {
    if (
      !(await confirm({
        title: "Remove playlist?",
        message: `Unlink “${pl.name}” from this advertiser? The playlist itself is not deleted.`,
        danger: false,
      }))
    )
      return;
    try {
      await detachPlaylistFromAdvertiser(advertiser.id, pl.id);
      refetchPlaylists();
      qc.invalidateQueries({ queryKey: ["advertisers"] });
      toast("Removed.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not remove."), "error");
    }
  }

  const selectedItems = items.filter((m) => selectedIds.has(m.id));
  const selectedCount = selectedItems.length;

  function toggleSelect(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function bulkDetach() {
    if (selectedCount === 0) return;
    if (
      !(await confirm({
        title: "Remove from advertiser?",
        message: `Unassign ${selectedCount} media item${selectedCount === 1 ? "" : "s"} from this advertiser? The files stay in the library.`,
        danger: false,
      }))
    )
      return;
    const results = await Promise.allSettled(
      selectedItems.map((m) => detachMediaFromAdvertiser(advertiser.id, m.id)),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    refetch();
    qc.invalidateQueries({ queryKey: ["media"] });
    qc.invalidateQueries({ queryKey: ["advertisers"] });
    clearSelection();
    if (failed) toast(`Removed ${selectedCount - failed}, ${failed} failed.`, "error");
    else toast(`Removed ${selectedCount} item${selectedCount === 1 ? "" : "s"}.`);
  }

  async function detach(mediaId: number) {
    if (
      !(await confirm({
        title: "Remove from advertiser?",
        message: "The media file will stay in the library but be unassigned from this advertiser.",
        danger: false,
      }))
    )
      return;
    try {
      await detachMediaFromAdvertiser(advertiser.id, mediaId);
      refetch();
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["advertisers"] });
      toast("Removed.");
    } catch (e) {
      toast(extractErrorMessage(e, "Could not remove."), "error");
    }
  }

  async function uploadNew(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadPct(0);
    try {
      await uploadMedia(Array.from(files), advertiser.id, (p) => setUploadPct(p.percent));
      refetch();
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["advertisers"] });
      toast(`Uploaded ${files.length} file${files.length === 1 ? "" : "s"}.`);
    } catch (e) {
      toast(extractErrorMessage(e, "Upload failed."), "error");
    } finally {
      setUploading(false);
    }
  }

  return (
    <>
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <button type="button" className="btn btn-outline" onClick={onBack}>
            ← All Advertisers
          </button>
          <span style={{ flex: 1 }} />
          {canEdit && (
            <>
              <button type="button" className="btn-action-edit" onClick={onEdit}>
                Edit
              </button>
              <button type="button" className="btn-action-delete" onClick={onDelete}>
                Delete
              </button>
            </>
          )}
        </div>
        <div className="adv-detail-header">
          <div className="adv-avatar" style={{ background: avatarColor(advertiser.name) }}>
            {initials(advertiser.name)}
          </div>
          <div className="adv-detail-info">
            <h3>{advertiser.name}</h3>
            <div className="adv-detail-meta">
              {advertiser.email && <span>✉ {advertiser.email}</span>}
              {advertiser.phone && <span>☎ {advertiser.phone}</span>}
              {advertiser.address && <span>📍 {advertiser.address}</span>}
            </div>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-header">
          <h3>
            Media{" "}
            <span style={{ fontWeight: 400, color: "var(--text2)", fontSize: 13 }}>
              {items.length}
            </span>
          </h3>
          {canEdit && (
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={onOpenAttach}>
                + Attach Existing
              </button>
              <label
                className="btn btn-primary btn-sm"
                style={{
                  cursor: uploading ? "default" : "pointer",
                  pointerEvents: uploading ? "none" : undefined,
                  opacity: uploading ? 0.7 : undefined,
                }}
              >
                {uploading ? `Uploading… ${uploadPct}%` : "⤴ Upload New"}
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  style={{ display: "none" }}
                  disabled={uploading}
                  onChange={(e) => {
                    uploadNew(e.target.files);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
          )}
        </div>
        {selectedCount > 0 && (
          <div className="bulk-bar">
            <span>{selectedCount} selected</span>
            <div className="bulk-actions">
              {canEdit && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  onClick={bulkDetach}
                >
                  Remove selected
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={clearSelection}
              >
                Clear
              </button>
            </div>
          </div>
        )}
        {items.length === 0 ? (
          <div className="empty">No media attached.</div>
        ) : (
          <div className="adv-media-grid">
            {items.map((m) => {
              const isSel = selectedIds.has(m.id);
              return (
                <div
                  key={m.id}
                  className={isSel ? "adv-media-item selected" : "adv-media-item"}
                  onClick={() => setPreview(m)}
                >
                  {m.mime_type?.startsWith("video") ? (
                    <video src={m.url} muted />
                  ) : (
                    <img src={m.url} alt={m.original_name} />
                  )}
                  <button
                    type="button"
                    className="select-check"
                    aria-pressed={isSel}
                    aria-label={`Select ${m.original_name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelect(m.id);
                    }}
                  >
                    {isSel ? "✓" : ""}
                  </button>
                  <div className="adv-media-name">{m.original_name}</div>
                  {canEdit && (
                    <button
                      type="button"
                      className="adv-media-remove"
                      aria-label="Remove from advertiser"
                      onClick={(e) => {
                        e.stopPropagation();
                        detach(m.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="card">
        <div className="card-header">
          <h3>
            Playlists{" "}
            <span style={{ fontWeight: 400, color: "var(--text2)", fontSize: 13 }}>
              {playlists.length}
            </span>
          </h3>
          {canEdit && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setPlaylistAttachOpen(true)}
            >
              + Attach Playlist
            </button>
          )}
        </div>
        {playlists.length === 0 ? (
          <div className="empty">
            No playlists attached. Attach playlists to organize this advertiser&apos;s campaigns.
          </div>
        ) : (
          <div className="adv-media-grid">
            {playlists.map((pl) => (
              <div
                key={pl.id}
                className="adv-media-item"
                onClick={() => setPreviewPlaylist(pl)}
                title={`Preview “${pl.name}”`}
              >
                {pl.thumbnail_url ? (
                  pl.thumbnail_type === "video" ? (
                    <video src={pl.thumbnail_url} muted />
                  ) : (
                    <img src={pl.thumbnail_url} alt={pl.name} />
                  )
                ) : (
                  <div className="adv-playlist-empty-thumb">🎞️</div>
                )}
                <div className="adv-media-name">{pl.name}</div>
                <div className="adv-playlist-count">
                  {pl.item_count ?? 0} item{(pl.item_count ?? 0) === 1 ? "" : "s"}
                </div>
                {canEdit && (
                  <button
                    type="button"
                    className="adv-media-remove"
                    aria-label="Remove playlist from advertiser"
                    onClick={(e) => {
                      e.stopPropagation();
                      detachPlaylist(pl);
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <MediaPreview item={preview} onClose={() => setPreview(null)} />
      <PlaylistPreview
        playlistId={previewPlaylist?.id ?? null}
        playlistName={previewPlaylist?.name}
        onClose={() => setPreviewPlaylist(null)}
      />
      <AttachMediaModal
        open={attachOpen}
        onClose={closeAttach}
        advertiserId={advertiser.id}
        onAttached={() => {
          refetch();
          qc.invalidateQueries({ queryKey: ["media"] });
          qc.invalidateQueries({ queryKey: ["advertisers"] });
        }}
      />
      <AttachPlaylistModal
        open={playlistAttachOpen}
        onClose={() => setPlaylistAttachOpen(false)}
        advertiserId={advertiser.id}
        attachedIds={new Set(playlists.map((p) => p.id))}
        onAttached={() => {
          refetchPlaylists();
          qc.invalidateQueries({ queryKey: ["advertisers"] });
        }}
      />
      {children}
    </>
  );
}

function AttachMediaModal({
  open,
  onClose,
  advertiserId,
  onAttached,
}: {
  open: boolean;
  onClose: () => void;
  advertiserId: number;
  onAttached: () => void;
}) {
  const { toast } = useToast();
  const [filter, setFilter] = useState("unassigned");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const { data: media = [] } = useQuery({
    queryKey: ["media", { advertiserFilter: filter === "unassigned" ? "unassigned" : "", search }],
    queryFn: () =>
      listMedia({
        advertiser: filter === "unassigned" ? "unassigned" : "",
        search,
      }),
    enabled: open,
  });

  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  async function confirmAttach() {
    if (selected.size === 0) return;
    try {
      await attachMediaToAdvertiser(advertiserId, Array.from(selected));
      onAttached();
      toast(`Attached ${selected.size} media.`);
      setSelected(new Set());
      onClose();
    } catch (e) {
      toast(extractErrorMessage(e, "Could not attach."), "error");
    }
  }

  return (
    <Modal open={open} onClose={onClose} width={640} ariaLabel="Attach media">
      <h3 style={{ marginBottom: 4 }}>Attach Media</h3>
      <p style={{ fontSize: 12, color: "var(--text2)", marginBottom: 14 }}>
        Click to select, then confirm.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <input
          className="form-input"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 260, padding: "8px 10px", fontSize: 13 }}
        />
        <select
          className="form-input"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ maxWidth: 200, padding: "8px 10px", fontSize: 13 }}
        >
          <option value="unassigned">Unassigned media</option>
          <option value="all">All media</option>
        </select>
        <span style={{ fontSize: 12, color: "var(--accent)", marginLeft: "auto" }}>
          {selected.size > 0 ? `${selected.size} selected` : ""}
        </span>
      </div>
      {media.length === 0 ? (
        <div className="empty">No matching media.</div>
      ) : (
        <div className="attach-grid">
          {media.map((m) => {
            const isSel = selected.has(m.id);
            return (
              <div
                key={m.id}
                className={isSel ? "attach-item selected" : "attach-item"}
                onClick={() => toggle(m.id)}
              >
                {m.mime_type?.startsWith("video") ? (
                  <video src={m.url} muted />
                ) : (
                  <img src={m.url} alt={m.original_name} />
                )}
                <button
                  type="button"
                  className="select-check"
                  aria-pressed={isSel}
                  aria-label={`Select ${m.original_name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(m.id);
                  }}
                >
                  {isSel ? "✓" : ""}
                </button>
                <button
                  type="button"
                  className="preview-btn"
                  aria-label={`Preview ${m.original_name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreview(m);
                  }}
                >
                  ⤢
                </button>
                <div className="attach-name">{m.original_name}</div>
              </div>
            );
          })}
        </div>
      )}
      <MediaPreview item={preview} onClose={() => setPreview(null)} />
      <div className="modal-actions">
        <button type="button" className="btn btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={selected.size === 0}
          onClick={confirmAttach}
        >
          Attach Selected
        </button>
      </div>
    </Modal>
  );
}

function AttachPlaylistModal({
  open,
  onClose,
  advertiserId,
  attachedIds,
  onAttached,
}: {
  open: boolean;
  onClose: () => void;
  advertiserId: number;
  attachedIds: Set<number>;
  onAttached: () => void;
}) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const { data: all = [] } = useQuery({
    queryKey: ["playlists"],
    queryFn: listPlaylists,
    enabled: open,
  });

  // Only offer playlists not already attached, filtered by the search box.
  const available = all.filter(
    (p) =>
      !attachedIds.has(p.id) &&
      (search.trim() === "" || p.name.toLowerCase().includes(search.trim().toLowerCase())),
  );

  function toggle(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  async function confirmAttach() {
    if (selected.size === 0) return;
    try {
      await attachPlaylistsToAdvertiser(advertiserId, Array.from(selected));
      onAttached();
      toast(`Attached ${selected.size} playlist${selected.size === 1 ? "" : "s"}.`);
      setSelected(new Set());
      onClose();
    } catch (e) {
      toast(extractErrorMessage(e, "Could not attach."), "error");
    }
  }

  return (
    <Modal open={open} onClose={onClose} width={560} ariaLabel="Attach playlist">
      <h3 style={{ marginBottom: 4 }}>Attach Playlist</h3>
      <p style={{ fontSize: 12, color: "var(--text2)", marginBottom: 14 }}>
        Click to select playlists to file under this advertiser.
      </p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <input
          className="form-input"
          placeholder="Search playlists…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280, padding: "8px 10px", fontSize: 13 }}
        />
        <span style={{ fontSize: 12, color: "var(--accent)", marginLeft: "auto" }}>
          {selected.size > 0 ? `${selected.size} selected` : ""}
        </span>
      </div>
      {available.length === 0 ? (
        <div className="empty">No playlists available to attach.</div>
      ) : (
        <div className="adv-playlist-pick-list">
          {available.map((p) => {
            const isSel = selected.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                className={isSel ? "adv-playlist-pick selected" : "adv-playlist-pick"}
                aria-pressed={isSel}
                onClick={() => toggle(p.id)}
              >
                <span className="adv-playlist-pick-check">{isSel ? "✓" : ""}</span>
                <span className="adv-playlist-pick-name">{p.name}</span>
                <span className="adv-playlist-pick-count">
                  {p.item_count ?? 0} item{(p.item_count ?? 0) === 1 ? "" : "s"}
                </span>
              </button>
            );
          })}
        </div>
      )}
      <div className="modal-actions">
        <button type="button" className="btn btn-outline" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={selected.size === 0}
          onClick={confirmAttach}
        >
          Attach Selected
        </button>
      </div>
    </Modal>
  );
}
